import { useMemo } from 'react';
import {
  Avatar,
  Box,
  Flex,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  Stack,
  Text,
} from '@chakra-ui/react';
import { Link } from 'react-router-dom';
import { NewDataGrid } from '../NewGridTable/NewDataGrid';
import type { ColumnDef } from '@tanstack/react-table';

interface ContributorsAndTeamsDetailsModalProps {
  openDrawer: boolean;
  handleDrawerClose: () => void;
  heading: string;
  subHeading: string;
  scope: string;
  data: string[] | null;
}

export const ContributorsAndTeamsDetailsModal = ({
  openDrawer,
  handleDrawerClose,
  heading,
  subHeading,
  scope,
  data,
}: ContributorsAndTeamsDetailsModalProps) => {
  const tableData = useMemo(
    () => (data ?? []).map((item) => ({ name: item })),
    [data]
  );

  const columns: ColumnDef<{ name: string }, any>[] = useMemo(
    () => [
      {
        header: scope === 'CONTRIBUTOR' ? 'Member' : 'Team',
        accessorKey: 'name',
        enableSorting: false,
        size: 300,
        cell: ({ row }: any) => {
          const name = row.original?.name;
          if (!name) return '-';
          const path =
            scope === 'CONTRIBUTOR'
              ? `/contributors/${name}`
              : `/teams/${name}`;
          return (
            <Flex align="center">
              <Avatar size="xs" name={name} />
              <Link to={path}>
                <Text
                  ml={2}
                  fontSize="sm"
                  fontFamily="heading"
                  color="text.secondary"
                  fontWeight="medium"
                  textDecoration="underline"
                  noOfLines={1}
                >
                  {name}
                </Text>
              </Link>
            </Flex>
          );
        },
      },
    ],
    [scope]
  );

  return (
    <Modal
      isOpen={openDrawer}
      onClose={handleDrawerClose}
      size="xl"
      isCentered
      scrollBehavior="inside"
      autoFocus={false}
    >
      <ModalOverlay />
      <ModalContent>
        <ModalHeader pb={2}>
          <Stack spacing={1}>
            <Text
              fontFamily="heading"
              fontSize="md"
              fontWeight="semibold"
              color="text.primary"
            >
              {heading}
            </Text>
            <Text
              fontFamily="heading"
              fontSize="sm"
              color="text.secondary"
              fontWeight="medium"
            >
              {subHeading}
            </Text>
          </Stack>
        </ModalHeader>
        <ModalCloseButton mt={2} />
        <ModalBody pb={6}>
          <Stack spacing={4}>
            {tableData.length > 0 ? (
              <Box
                borderWidth="1px"
                borderRadius="md"
                overflow="hidden"
                w="100%"
              >
                <NewDataGrid
                  data={tableData}
                  columns={columns}
                  showLoader={false}
                  skeletonLoader={false}
                  sticky="none"
                  useMaxH
                  showGridLines
                />
              </Box>
            ) : (
              <Box
                borderWidth="1px"
                borderRadius="md"
                overflow="hidden"
                w="100%"
                minH="200px"
                display="flex"
                alignItems="center"
                justifyContent="center"
              >
                <Text
                  fontFamily="heading"
                  fontSize="sm"
                  color="text.secondary"
                  fontWeight="medium"
                >
                  No data found
                </Text>
              </Box>
            )}
          </Stack>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};
